/**
 * The TypeScript mirror of `PointsExpression` — §4.3's grammar, its guards, and the same errors.
 *
 * <b>Why this exists at all.</b> §9.4 asks for two things a server round trip cannot give: an
 * inline error carrying the character position, as the operator types, and a live evaluation table
 * showing what the formula does to four sample finishes. Both need the expression parsed and run
 * locally. It is the same relationship `validate.ts` has to the server's validators — **feedback,
 * never the control**. `PointsExpression.Parse` still decides on write, and the preview endpoint
 * still produces the standings; nothing here is trusted with either.
 *
 * <b>Keep it in step with `PointsExpression.cs`.</b> Same variables, same functions, same arities,
 * same guards, same precedence. A disagreement shows up in the worst available way: an expression
 * this file accepts and the server refuses is a save that fails with the operator looking at a
 * green form, and an expression this file evaluates differently is a preview table that lies about
 * what the board will do.
 *
 * <b>No `eval`, `new Function`, or anything else that reaches the host</b> — for the same reason
 * the server has none. The content is operator-authored, and here it would run in another
 * operator's browser.
 */

// ---------------------------------------------------------------- the contract

/** §4.3's nine variables, in the order the specification lists them. */
export const EXPRESSION_VARIABLES = [
  'place',
  'effectivePlace',
  'fieldSize',
  'uniquePlayers',
  'cashers',
  'buyIn',
  'prizePool',
  'prizeWon',
  'isDeal',
] as const;

export type ExpressionVariable = (typeof EXPRESSION_VARIABLES)[number];

/** §4.3's whitelisted functions. */
export const EXPRESSION_FUNCTIONS = [
  'min',
  'max',
  'abs',
  'round',
  'floor',
  'ceil',
  'sqrt',
  'pow',
  'log',
  'ln',
  'log10',
  'root',
  'clamp',
] as const;

export type ExpressionFunction = (typeof EXPRESSION_FUNCTIONS)[number];

/** §4.3: expression length ≤ 500 characters, AST depth ≤ 20, result clamped to [0, 1e9]. */
export const MAX_EXPRESSION_LENGTH = 500;
export const MAX_EXPRESSION_DEPTH = 20;
export const MAX_EXPRESSION_RESULT = 1e9;

/**
 * The parser's own recursion cap, which the depth check does not provide: `((((1))))` is a tree of
 * depth 1 and four parser frames deep. Mirrors `PointsExpression.MaxNesting`, and like it the
 * length cap means this never rejects anything the depth check would have accepted.
 */
const MAX_NESTING = 200;

/** One value per §4.3 variable. Every field is required — a formula sees exactly these nine. */
export type ExpressionContext = Record<ExpressionVariable, number>;

// ---------------------------------------------------------------- the result of parsing

export interface ExpressionError {
  message: string;
  /** Zero-based index into the source, for §9.4's inline error. Null when it is about the whole. */
  position: number | null;
}

export interface ParsedExpression {
  ok: true;
  source: string;
  /** Which of the nine it actually reads — §9.4's palette marks these, and the sample table names them. */
  variables: ExpressionVariable[];
  evaluate: (context: ExpressionContext) => ExpressionResult;
}

export interface FailedExpression {
  ok: false;
  error: ExpressionError;
}

export type ExpressionParse = ParsedExpression | FailedExpression;

/**
 * What one evaluation produced, and whether a guard had to rescue it.
 *
 * `warning` mirrors the server's: it is set only by §4.3's non-finite rule, because "you scored
 * nothing" and "the formula broke on your row" are different answers to the same question. Division
 * by zero and `ln` of a non-positive number are *defined* to be 0 and warn about nothing.
 */
export interface ExpressionResult {
  value: number;
  warning: string | null;
}

// ---------------------------------------------------------------- AST

type Node =
  | { kind: 'number'; value: number }
  | { kind: 'variable'; name: ExpressionVariable }
  | { kind: 'unary'; negate: boolean; operand: Node }
  | { kind: 'binary'; operator: string; left: Node; right: Node }
  | { kind: 'call'; name: ExpressionFunction; args: Node[] };

class ParseFailure extends Error {
  /**
   * Written out rather than declared as a constructor parameter property, which is the one piece
   * of TypeScript syntax Node's `--experimental-strip-types` cannot erase (it would have to emit
   * an assignment, not delete a type). This module is run directly by Node so that the mirror can
   * be checked against `PointsExpression.cs` by a command rather than by an afternoon — see
   * `packages/ui/tsconfig.json`. Behaviour is identical.
   */
  readonly position: number | null;

  constructor(message: string, position: number | null) {
    super(message);
    this.position = position;
  }
}

// ---------------------------------------------------------------- parsing

/**
 * Parses, reporting the first problem and where it is.
 *
 * An empty expression is an error rather than a zero: the schema requires at least one character,
 * so a blank box is a document the server will refuse, and saying so is more useful than scoring
 * everybody nothing.
 */
export function parseExpression(source: string): ExpressionParse {
  if (source.trim().length === 0) {
    return { ok: false, error: { message: 'The formula is empty.', position: null } };
  }

  if (source.length > MAX_EXPRESSION_LENGTH) {
    return {
      ok: false,
      error: {
        message: `The formula is ${source.length} characters long; the limit is ${MAX_EXPRESSION_LENGTH}.`,
        position: MAX_EXPRESSION_LENGTH,
      },
    };
  }

  try {
    const root = new Parser(source).parseAll();
    const depth = depthOf(root);

    if (depth > MAX_EXPRESSION_DEPTH) {
      return {
        ok: false,
        error: {
          message: `The formula nests ${depth} levels deep; the limit is ${MAX_EXPRESSION_DEPTH}.`,
          position: null,
        },
      };
    }

    const variables = EXPRESSION_VARIABLES.filter((name) => reads(root, name));

    return {
      ok: true,
      source,
      variables,
      evaluate: (context) => finish(evaluate(root, context)),
    };
  } catch (error) {
    if (error instanceof ParseFailure) {
      return { ok: false, error: { message: error.message, position: error.position } };
    }
    throw error;
  }
}

/**
 * Recursive descent over §4.3's grammar, lexing as it goes — 500 characters at most, so a separate
 * token pass would buy nothing. Identical in shape to the server's, deliberately.
 *
 * ```
 * expression := term (('+' | '-') term)*
 * term       := unary (('*' | '/' | '%') unary)*
 * unary      := ('+' | '-') unary | primary
 * primary    := number | variable | function '(' expression (',' expression)* ')' | '(' expression ')'
 * ```
 */
class Parser {
  private position = 0;
  private nesting = 0;

  // Written out rather than a constructor parameter property, for the reason `ParseFailure` above
  // gives: strip-only type erasure cannot emit the assignment one implies.
  private readonly source: string;

  constructor(source: string) {
    this.source = source;
  }

  parseAll(): Node {
    const node = this.parseExpression();
    this.skipWhitespace();

    if (this.position < this.source.length) {
      throw new ParseFailure(
        `Unexpected “${this.source[this.position]}”.`,
        this.position,
      );
    }

    return node;
  }

  private parseExpression(): Node {
    let left = this.parseTerm();

    for (;;) {
      this.skipWhitespace();
      if (this.position >= this.source.length) return left;

      const operator = this.source[this.position]!;
      if (operator !== '+' && operator !== '-') return left;

      this.position += 1;
      left = { kind: 'binary', operator, left, right: this.parseTerm() };
    }
  }

  private parseTerm(): Node {
    let left = this.parseUnary();

    for (;;) {
      this.skipWhitespace();
      if (this.position >= this.source.length) return left;

      const operator = this.source[this.position]!;
      if (operator !== '*' && operator !== '/' && operator !== '%') return left;

      this.position += 1;
      left = { kind: 'binary', operator, left, right: this.parseUnary() };
    }
  }

  private parseUnary(): Node {
    this.skipWhitespace();
    const c = this.source[this.position];

    if (c === '+' || c === '-') {
      this.position += 1;
      return this.enter(() => ({
        kind: 'unary' as const,
        negate: c === '-',
        operand: this.parseUnary(),
      }));
    }

    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    this.skipWhitespace();

    if (this.position >= this.source.length) {
      throw new ParseFailure('The formula ends unexpectedly.', this.position);
    }

    const c = this.source[this.position]!;

    if (c === '(') {
      this.position += 1;
      const inner = this.enter(() => this.parseExpression());
      this.expect(')');
      return inner;
    }

    if (isDigit(c) || c === '.') return this.parseNumber();
    if (isLetter(c) || c === '_') return this.parseIdentifier();

    throw new ParseFailure(`Unexpected “${c}”.`, this.position);
  }

  private parseNumber(): Node {
    const start = this.position;

    while (this.position < this.source.length && (isDigit(this.source[this.position]!) || this.source[this.position] === '.')) {
      this.position += 1;
    }

    // Exponent notation, so 1e9 — the result cap — is expressible inside 500 characters.
    const exponent = this.source[this.position];
    if (exponent === 'e' || exponent === 'E') {
      const save = this.position;
      this.position += 1;

      const sign = this.source[this.position];
      if (sign === '+' || sign === '-') this.position += 1;

      if (this.position < this.source.length && isDigit(this.source[this.position]!)) {
        while (this.position < this.source.length && isDigit(this.source[this.position]!)) this.position += 1;
      } else {
        this.position = save; // a trailing 'e' belongs to whatever follows, not to the number
      }
    }

    const text = this.source.slice(start, this.position);
    const value = Number(text);

    if (!Number.isFinite(value)) {
      throw new ParseFailure(`“${text}” is not a number.`, start);
    }

    return { kind: 'number', value };
  }

  private parseIdentifier(): Node {
    const start = this.position;

    while (
      this.position < this.source.length &&
      (isLetter(this.source[this.position]!) || isDigit(this.source[this.position]!) || this.source[this.position] === '_')
    ) {
      this.position += 1;
    }

    const name = this.source.slice(start, this.position);
    this.skipWhitespace();

    if (this.source[this.position] === '(') return this.parseCall(name, start);

    // Matched exactly rather than case-insensitively, like the server: `FieldSize` is a typo, and
    // accepting it would make the variable list a suggestion rather than the contract it is.
    if (!(EXPRESSION_VARIABLES as readonly string[]).includes(name)) {
      throw new ParseFailure(
        `“${name}” isn’t one of the values a formula can use.`,
        start,
      );
    }

    return { kind: 'variable', name: name as ExpressionVariable };
  }

  private parseCall(name: string, start: number): Node {
    if (!(EXPRESSION_FUNCTIONS as readonly string[]).includes(name)) {
      throw new ParseFailure(`“${name}” isn’t one of the functions a formula can use.`, start);
    }

    this.position += 1; // '('
    const args: Node[] = [];

    this.skipWhitespace();
    if (this.source[this.position] === ')') {
      this.position += 1;
    } else {
      for (;;) {
        args.push(this.enter(() => this.parseExpression()));
        this.skipWhitespace();

        if (this.source[this.position] === ',') {
          this.position += 1;
          continue;
        }

        this.expect(')');
        break;
      }
    }

    checkArity(name as ExpressionFunction, args.length, start);
    return { kind: 'call', name: name as ExpressionFunction, args };
  }

  private expect(c: string): void {
    this.skipWhitespace();

    if (this.source[this.position] !== c) {
      throw new ParseFailure(
        this.position < this.source.length
          ? `Expected “${c}” here, but found “${this.source[this.position]}”.`
          : `Expected “${c}”, but the formula ends.`,
        this.position,
      );
    }

    this.position += 1;
  }

  private enter<T>(parse: () => T): T {
    this.nesting += 1;
    if (this.nesting > MAX_NESTING) {
      throw new ParseFailure(`The formula nests more than ${MAX_NESTING} levels deep.`, this.position);
    }

    try {
      return parse();
    } finally {
      this.nesting -= 1;
    }
  }

  private skipWhitespace(): void {
    while (this.position < this.source.length && /\s/.test(this.source[this.position]!)) {
      this.position += 1;
    }
  }
}

/**
 * Arity is checked at parse time rather than at evaluation, exactly as the server does it:
 * `pow(2)` should be refused while its author is looking at it, not silently score somebody
 * nothing a fortnight later.
 */
function checkArity(name: ExpressionFunction, count: number, position: number): void {
  const rules: Record<ExpressionFunction, { ok: (n: number) => boolean; expected: string }> = {
    min: { ok: (n) => n >= 2, expected: 'at least 2 values' },
    max: { ok: (n) => n >= 2, expected: 'at least 2 values' },
    abs: { ok: (n) => n === 1, expected: '1 value' },
    floor: { ok: (n) => n === 1, expected: '1 value' },
    ceil: { ok: (n) => n === 1, expected: '1 value' },
    sqrt: { ok: (n) => n === 1, expected: '1 value' },
    ln: { ok: (n) => n === 1, expected: '1 value' },
    log10: { ok: (n) => n === 1, expected: '1 value' },
    round: { ok: (n) => n === 1 || n === 2, expected: '1 or 2 values' },
    log: { ok: (n) => n === 1 || n === 2, expected: '1 or 2 values' },
    pow: { ok: (n) => n === 2, expected: '2 values' },
    root: { ok: (n) => n === 2, expected: '2 values' },
    clamp: { ok: (n) => n === 3, expected: '3 values' },
  };

  const rule = rules[name];
  if (rule.ok(count)) return;

  throw new ParseFailure(
    `“${name}” takes ${rule.expected}, but was given ${count}.`,
    position,
  );
}

// ---------------------------------------------------------------- evaluation

/**
 * §4.3's guards, in one place. Never throws: every one of them yields a number, because a
 * leaderboard that stops calculating because one player divided by zero is worse than one that
 * scores them nothing and says so.
 */
function finish(value: number): ExpressionResult {
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return {
      value: 0,
      warning: `The formula produced ${
        Number.isNaN(value) ? 'an undefined value' : 'an infinite value'
      } here, so this result would score 0.`,
    };
  }

  return { value: Math.min(Math.max(value, 0), MAX_EXPRESSION_RESULT), warning: null };
}

function evaluate(node: Node, context: ExpressionContext): number {
  switch (node.kind) {
    case 'number':
      return node.value;
    case 'variable':
      return context[node.name];
    case 'unary': {
      const operand = evaluate(node.operand, context);
      return node.negate ? -operand : operand;
    }
    case 'binary':
      return apply(node.operator, evaluate(node.left, context), evaluate(node.right, context));
    case 'call':
      return call(node.name, node.args.map((arg) => evaluate(arg, context)));
  }
}

function apply(operator: string, left: number, right: number): number {
  switch (operator) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '*':
      return left * right;

    // §4.3: division by zero yields 0. Checked rather than left to IEEE, which gives Infinity for
    // x/0 and NaN for 0/0 — two different wrong answers where the specification asks for one.
    case '/':
      return right === 0 ? 0 : left / right;
    case '%':
      return right === 0 ? 0 : left % right;
    default:
      return 0;
  }
}

function call(name: ExpressionFunction, args: number[]): number {
  const [a = 0, b = 0, c = 0] = args;

  switch (name) {
    case 'min':
      return Math.min(...args);
    case 'max':
      return Math.max(...args);
    case 'abs':
      return Math.abs(a);
    case 'floor':
      return Math.floor(a);
    case 'ceil':
      return Math.ceil(a);

    // Away from zero, matching the server: this is a number an operator reads, and round(0.5)
    // answering 0 is the kind of surprise that gets reported as a bug.
    case 'round':
      return args.length === 1 ? roundHalfUp(a, 0) : roundHalfUp(a, Math.min(Math.max(b, 0), 15));
    case 'sqrt':
      return a < 0 ? 0 : Math.sqrt(a);
    case 'pow':
      return Math.pow(a, b);

    // §4.3: log/ln of ≤ 0 yields 0. Without this the natural answer is -Infinity, which clamps to
    // 0 anyway but warns that the formula broke — it did not.
    case 'ln':
      return a <= 0 ? 0 : Math.log(a);
    case 'log10':
      return a <= 0 ? 0 : Math.log10(a);
    case 'log':
      if (args.length === 1) return a <= 0 ? 0 : Math.log(a);
      return a <= 0 || b <= 0 || b === 1 ? 0 : Math.log(a) / Math.log(b);

    // The nth root, which is what makes the WSOP-style formulas expressible. A zero degree is
    // division by zero in the exponent, so it takes the same answer as '/'.
    case 'root':
      return b === 0 || a < 0 ? 0 : Math.pow(a, 1 / b);
    case 'clamp':
      return b > c ? b : Math.min(Math.max(a, b), c);
  }
}

/** .NET's `MidpointRounding.AwayFromZero`, which JavaScript has no built-in for. */
function roundHalfUp(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  const scaled = value * factor;
  const rounded = Math.sign(scaled) * Math.round(Math.abs(scaled));
  return rounded / factor;
}

// ---------------------------------------------------------------- walking the tree

function depthOf(node: Node): number {
  switch (node.kind) {
    case 'unary':
      return 1 + depthOf(node.operand);
    case 'binary':
      return 1 + Math.max(depthOf(node.left), depthOf(node.right));
    case 'call':
      return 1 + Math.max(...node.args.map(depthOf));
    default:
      return 1;
  }
}

function reads(node: Node, name: ExpressionVariable): boolean {
  switch (node.kind) {
    case 'variable':
      return node.name === name;
    case 'unary':
      return reads(node.operand, name);
    case 'binary':
      return reads(node.left, name) || reads(node.right, name);
    case 'call':
      return node.args.some((arg) => reads(arg, name));
    default:
      return false;
  }
}

// ---------------------------------------------------------------- §9.4's sample inputs

export interface ExpressionSample {
  /** "10th of 500" — how the row reads. */
  label: string;
  place: number;
  fieldSize: number;
}

/**
 * The four §9.4 names, and they are well chosen: two of them differ only in field size and two
 * only in finish, so a formula that ignores either one is visible as two identical rows.
 */
export const EXPRESSION_SAMPLES: readonly ExpressionSample[] = [
  { label: '1st of 50', place: 1, fieldSize: 50 },
  { label: '1st of 500', place: 1, fieldSize: 500 },
  { label: '10th of 500', place: 10, fieldSize: 500 },
  { label: '100th of 1,000', place: 100, fieldSize: 1000 },
];

/**
 * One sample finish as the nine numbers a formula actually sees.
 *
 * §9.4's shorthand fixes two of them. The other seven have to come from somewhere, and the honest
 * answer is that they are **modelled** — so the editor prints the ones the formula reads rather
 * than leaving an operator to wonder where a `prizeWon` came from:
 *
 * - `effectivePlace` equals `place` and `isDeal` is 0: the sample did not end in a chop. The two
 *   variables differ only in a chopped event, which is a case worth trying deliberately rather
 *   than one to bake into every row.
 * - `uniquePlayers` equals `fieldSize`: no re-entries.
 * - `cashers` is 15% of the field, rounded up — the usual depth a room pays to.
 * - `prizePool` is buy-in × entries, with no rake taken out.
 * - `prizeWon` splits that pool over the paid places on a 1 / place^1.4 decay, normalised so the
 *   paid places sum to it. **A shape, not any room's payout schedule** — enough that a formula
 *   reading `prizeWon` shows a plausible curve instead of one flat number.
 */
export function sampleContext(sample: ExpressionSample, buyIn: number): ExpressionContext {
  const cashers = Math.max(1, Math.ceil(sample.fieldSize * 0.15));
  const prizePool = sample.fieldSize * buyIn;

  return {
    place: sample.place,
    effectivePlace: sample.place,
    fieldSize: sample.fieldSize,
    uniquePlayers: sample.fieldSize,
    cashers,
    buyIn,
    prizePool,
    prizeWon: sample.place <= cashers ? prizePool * payoutShare(sample.place, cashers) : 0,
    isDeal: 0,
  };
}

function payoutShare(place: number, cashers: number): number {
  const weight = (p: number) => 1 / p ** 1.4;

  let total = 0;
  for (let p = 1; p <= cashers; p++) total += weight(p);

  return weight(place) / total;
}

// ---------------------------------------------------------------- lexing helpers

function isDigit(c: string): boolean {
  return c >= '0' && c <= '9';
}

function isLetter(c: string): boolean {
  return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');
}
